import React from "react";
import _, { cloneDeep, get, has, last, merge, mergeWith } from "lodash";
import memoizeOne from "memoize-one";
import Icon from "../../../common/Icon";
import List from "../../../common/List/List";
import Panel from "../../../common/Panel/Panel";
import connectToMedia from "../../Media/connectToMedia";
import Range from "../../../utilities/transcriptLib/Range.js";
import {
  CategoryIcon,
  EntityIcon,
  EyeIcon,
  KeywordIcon,
  PhraseSpottingIcon,
  VerbIcon,
} from "../../../assets/icons/index";
const PanelName = {
  Categories: "Categories",
  KeyWord: "KeyWord",
  Verb: "Verb",
  Phrase_Spotting: "Phrase Spotting",
  SelectedCat: "SelectedCat",
  Entities: "Entities",
};
const getCount = (keyword) => {
  return Object.values(keyword.t).reduce((acc, speakerHits) => {
    return acc + speakerHits.length;
  }, 0);
};

const getHits = (keyword) => {
  if (!keyword.t && keyword.occurrences) {
    const test = keyword.occurrences.reduce((acc, item) => {
      if (item.hits) {
        return acc.concat(item.hits);
      }
      return [...acc, item];
    }, []);
    return test;
  }

  if (!keyword.t && keyword.hits) {
    const test = keyword.hits.reduce((acc, item) => {
      return [...acc, item];
    }, []);
    return test;
  }

  return Object.keys(keyword.t)
    .map((speaker) =>
      keyword.t[speaker].map((time) => ({ s: time, w: speaker }))
    )
    .reduce((acc, times) => [...acc, ...times], []);
};

class Keywords extends React.PureComponent {
  static defaultProps = {
    queryState: {
      hits: [],
      queries: [],
    },
  };

  state = {
    selection: null,
    categories: [],
  };

  componentDidMount() {
    if (document.getElementById("exHits")) {
      document.getElementById("exHits").getElementsByTagName("i")[0].click();
    }
    this.setState({
      categories: this.props.categories,
    });
  }

  getKeywordSelections = memoizeOne((queries) =>
    queries
      .filter((query) => query.type === "keywords")
      .map((query) => query.parent)
  );

  handleChange = (selected, deselected) => {
    const ranges = [];
    let isExternalHits = false;

    selected.forEach((obj) => {
      const occurrences = get(obj, "occurrences", [obj]);
      occurrences.forEach((occ) => {
        if (occ.start && occ.end) {
          for (const i in occ.start) {
            const tempRange = new Range({
              start: occ.start[i],
              end: occ.end[i],
              name: occ.exact,
            });
            ranges.push(tempRange);
          }
        }
        if (occ.s && occ.e) {
          const tempRange = new Range({
            start: occ.s,
            end: occ.e,
            name: obj.text,
          });
          ranges.push(tempRange);
        }
      });
    });

    if (ranges.length) {
      this.props.markTranscript(ranges);
    } else {
      this.props.markTranscript(ranges);
    }

    if (has(selected[0], "hits")) {
      selected[0].hits.map((hit) => {
        if (has(hit, "s")) {
          for (const i in hit.s) {
            const tempEnd = get(hit, `e[${i}]`, hit.s[i]);
            const tempRange = new Range({
              start: hit.s[i],
              end: tempEnd,
              name: hit.label,
            });
            ranges.push(tempRange);
          }
        }
      });
      isExternalHits = true;
    } else {
      isExternalHits = false;
    }

    const { onAddQuery, onRemoveQuery, onSeek } = this.props;
    selected.forEach((item) => {
      const hits =
        (item.type === "category" || item.type === "unsorted") &&
        item.keywords.length
          ? item.keywords.reduce(
              (acc, keyword) => [...acc, ...getHits(keyword)],
              []
            )
          : getHits(item);
      if (!ranges.length && hits.length && !isExternalHits) {
        hits.map((obj) => {
          const tempRange = new Range({
            start: obj.s,
            end: obj.s,
            name: selected.name,
          });
          ranges.push(tempRange);
        });
        this.props.markTranscript(ranges);
      } else {
        this.props.markTranscript(ranges);
      }

      // keyword hits are not always in chronological order
      const sortedHits = hits.sort((a, b) => (a.s < b.s ? -1 : 1)) || [];
      if (sortedHits.length && selected.length) {
        onSeek(sortedHits[0]);
      }
      onAddQuery({
        parent: item,
        hits: sortedHits,
        label: item.name || item.text,
        icon: item.type === "category" ? "tag" : "message",
        type: "keywords",
      });
    });

    deselected.forEach((item) => {
      onRemoveQuery(item);
    });
  };

  setPanelsIcon = (name) => {
    switch (name) {
      case PanelName.Categories:
        return <CategoryIcon />;
      case PanelName.Verb:
        return <VerbIcon />;
      case PanelName.KeyWord:
        return <KeywordIcon />;
      case PanelName.Phrase_Spotting:
        return <PhraseSpottingIcon />;
      case PanelName.SelectedCat:
        return <EyeIcon />;
      case PanelName.Entities:
        return <EntityIcon />;
      default:
        break;
    }
  };

  render() {
    const {
      topics = [],
      keyWords,
      verbs,
      groups = [],
      queries = [],
      categories = [],
      externalHits = [],
      entities = [],
      isKeywordAnalyzerOn,
      isPhraseSupportingAnalyzerOn,
      isVerbNounAnalyzerOn,
      isEntitiesAnalyzerOn,
      llmCategories = [],
    } = this.props;
    /***
      SORTS VERB NOUN PAIRS
    ***/
    const aggregateVerbNounPairs = [];
    const clonedVerbs = cloneDeep(verbs);
    clonedVerbs.forEach((verb) => {
      // current verbNounPair already exists in aggregate
      if (
        aggregateVerbNounPairs.some((aggVerb) => aggVerb.name === verb.name)
      ) {
        const clonedVerb = cloneDeep(verb);
        const matchingVerb = aggregateVerbNounPairs.find(
          (aggVerb) => aggVerb.name === verb.name
        );

        matchingVerb.speakers = Array.from(
          new Set([...matchingVerb.speakers, ...clonedVerb.speakers])
        );

        matchingVerb.occurrences = [
          ...matchingVerb.occurrences,
          ...clonedVerb.occurrences,
        ];
      } else {
        aggregateVerbNounPairs.push(verb);
      }
    });

    const sortNestedByInnerOccurrence = (arr) => {
      arr.forEach((el) => {
        return el.occurrences.sort((a, b) => a.start[0] - b.start[0]);
      });
    };

    const sortListByOccurrences = (arr) => {
      arr.sort((a, b) => {
        return a.occurrences[0].start[0] - b.occurrences[0].start[0];
      });
    };

    sortNestedByInnerOccurrence(aggregateVerbNounPairs);
    sortListByOccurrences(aggregateVerbNounPairs);

    /***
      SORTS CATEGORIES
    ***/
    const sortedCategories = cloneDeep(categories);
    const removedCats = sortedCategories.filter(
      (cat) => cat.keywords.length === 0
    );
    _.remove(sortedCategories, (cat) => cat.keywords.length === 0);

    const sortNestedByInnerKeywords = (arr) => {
      arr.forEach((el) => {
        return el.keywords.sort(
          (a, b) => a?.t?.speaker?.[0] - b?.t?.speaker?.[0]
        );
      });
    };

    const sortListByKeywords = (arr) => {
      arr.sort(
        (a, b) => a.keywords[0]?.t?.speaker[0] - b.keywords[0]?.t?.speaker[0]
      );
    };
    sortNestedByInnerOccurrence(sortedCategories);
    sortListByOccurrences(sortedCategories);

    const allSortedCategories = sortedCategories.concat(removedCats);

    const sortedLlmCategories = cloneDeep(llmCategories);
    const removedllmCats = sortedLlmCategories.filter(
      (cat) => cat.keywords.length === 0
    );
    _.remove(sortedLlmCategories, (cat) => cat.keywords.length === 0);
    sortNestedByInnerOccurrence(sortedLlmCategories);
    sortListByOccurrences(sortedLlmCategories);
    const allSortedllmCategories = sortedLlmCategories.concat(removedllmCats);

    /***
      SORTS TOPICS(KEYWORDS)
    ***/
    const sortKeywords = (arr) => {
      arr.forEach((el) => {
        el.startTimes = [];
        el.keywords.forEach((kw) => {
          Object.entries(kw.t).forEach(([k, v]) => {
            el.startTimes.push(...v);
          });
        });

        el.startTimes.sort((a, b) => a - b);
      });

      arr.sort((a, b) => a.startTimes[0] - b.startTimes[0]);
    };

    const sortNestedKeywords = (arr) => {
      arr.forEach((el) => {
        el.keywords.forEach((kw) => {
          kw.startTimes = [];
          Object.entries(kw.t).forEach(([k, v]) => {
            kw.startTimes.push(...v);
          });
          kw.startTimes.sort((a, b) => a - b);
        });

        el.keywords.sort((a, b) => a.startTimes[0] - b.startTimes[0]);
      });
    };

    sortKeywords(topics);
    sortNestedKeywords(topics);

    sortKeywords(groups);
    sortNestedKeywords(groups);

    /*
    SORT ENTITIES
    */
    const sortKeywordsByOccurrence = (arr) => {
      arr.forEach((el) => {
        el.startTimes = [];
        el.keywords.forEach((occ) => {
          el.startTimes.push(occ.occurrences.s);
          occ.occurrences.sort((a, b) => a.s - b.s);
        });

        el.startTimes.sort((a, b) => a - b);
      });

      arr.forEach((el) => {
        el.keywords.sort(
          (a, b) =>
            a.keywords?.[0].occurrences[0].s - b.keywords?.[0].occurrences[0].s
        );
      });
    };

    const sortEntity = (arr) => {
      arr.sort(
        (a, b) => a.keywords?.startTimes?.[0] - b.keywords?.startTimes?.[0]
      );
    };

    sortKeywordsByOccurrence(entities);
    sortEntity(entities);
    return (
      <Panel>
        <List
          onChange={this.handleChange}
          selections={this.getKeywordSelections(queries)}

        >
          {get(externalHits, "0.hits.length") > 0 ? (
            <List.Section
              id="exHits"
              title={externalHits[0].SectionTitle || "Selected Hits"}
            >
              {externalHits.map((eHits) => (
                <List.Node
                  key={eHits.groupTitle}
                  icon={<Icon type="tag" theme="filled" />}
                  title={eHits.groupTitle || "All Hits"}
                  value={eHits}
                  badge={eHits.hits.reduce(
                    (total, value) => total + value.s.length,
                    0
                  )}
                >
                  {eHits.keywords.length
                    ? eHits.keywords.map((keyword) => (
                        <List.Node
                          key={keyword.name}
                          icon={<Icon type="message" theme="filled" />}
                          title={keyword.name}
                          value={keyword}
                          badge={getCount(keyword)}
                        />
                      ))
                    : null}
                </List.Node>
              ))}
            </List.Section>
          ) : null}
          <List.Section
            title="Categories"
            icon={this.setPanelsIcon(PanelName.Categories)}
          >
            {/* <S.SearchContainer>
              <div id="search-field">
                <input
                  type="search"
                  id="header-search"
                  placeholder="Search..."
                />
                <svg
                  width="21"
                  height="21"
                  viewBox="0 0 21 21"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9.13379 16.5439C12.7582 16.5439 15.6963 13.6058 15.6963 9.98145C15.6963 6.35708 12.7582 3.41895 9.13379 3.41895C5.50942 3.41895 2.57129 6.35708 2.57129 9.98145C2.57129 13.6058 5.50942 16.5439 9.13379 16.5439Z"
                    stroke="#677791"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M13.7739 14.6221L17.5708 18.419"
                    stroke="#677791"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </div>
            </S.SearchContainer> */}

            {allSortedCategories.map((category) => (
              <List.Node
                key={category.name}
                // icon={<Icon type="tag" theme="filled" />}
                icon={this.setPanelsIcon(PanelName.SelectedCat)}
                title={last(category.name.split("_").filter((i) => i))}
                value={category}
                badge={category.keywords.reduce(
                  (acc, keyword) => getCount(keyword) + acc,
                  0
                )}
              >
                {category?.occurrences?.map((keyword) => (
                  <List.Node
                    key={keyword?.exact}
                    icon={this.setPanelsIcon(PanelName.SelectedCat)}
                    title={keyword.exact}
                    value={keyword}
                    // badge={getCount(keyword)}
                    badge={keyword.hits?.length}
                  />
                ))}
              </List.Node>
            ))}
          </List.Section>
{/* llmcategories */}
          <List.Section
            title="Advanced Categories"
            icon={this.setPanelsIcon(PanelName.Categories)}
          >
            {allSortedllmCategories.map((category) => (
              <List.Node
                key={category.name}
                icon={this.setPanelsIcon(PanelName.SelectedCat)}
                title={last(category.name.split("_").filter((i) => i))}
                value={category}
                badge={category?.categoryValue ? category?.categoryValue : 0 }
                isClickable={false }
              >
                
              </List.Node>
            ))}
          </List.Section>

          {isVerbNounAnalyzerOn && (
            <>
              <hr className="panel-hr-line" />
              <List.Section
                title="VERB/NOUNS"
                icon={this.setPanelsIcon(PanelName.Verb)}
              >
                {aggregateVerbNounPairs.map((category) => (
                  <List.Node
                    key={category.name}
                    icon={this.setPanelsIcon(PanelName.SelectedCat)}
                    title={category.name}
                    value={category}
                    badge={category.occurrences.reduce(
                      (total, value) => total + value.start.length,
                      0
                    )}
                  />
                ))}
              </List.Section>
            </>
          )}

          {isKeywordAnalyzerOn && (
            <>
              <hr className="panel-hr-line" />
              <List.Section
                title="KEYWORDS"
                icon={this.setPanelsIcon(PanelName.KeyWord)}
              >
                {topics.map((topic) => (
                  <List.Node
                    key={topic.name}
                    // icon={<Icon type="tag" theme="filled" />}
                    icon={this.setPanelsIcon(PanelName.SelectedCat)}
                    title={last(topic.name.split("_").filter((i) => i))}
                    value={topic}
                    badge={topic.keywords.reduce(
                      (acc, keyword) => getCount(keyword) + acc,
                      0
                    )}
                  >
                    {topic.keywords.map((keyword) => (
                      <List.Node
                        key={keyword.name}
                        icon={this.setPanelsIcon(PanelName.SelectedCat)}
                        title={keyword.name}
                        value={keyword}
                        badge={getCount(keyword)}
                      />
                    ))}
                  </List.Node>
                ))}
              </List.Section>
            </>
          )}

          {isPhraseSupportingAnalyzerOn && (
            <>
              <hr className="panel-hr-line" />
              <List.Section
                title="Phrase Spotting"
                icon={this.setPanelsIcon(PanelName.Phrase_Spotting)}
              >
                {groups.map((group) => (
                  <List.Node
                    key={group.name}
                    icon={this.setPanelsIcon(PanelName.SelectedCat)}
                    title={group.name}
                    value={group}
                    badge={group.keywords.reduce(
                      (acc, keyword) => getCount(keyword) + acc,
                      0
                    )}
                  >
                    {group.keywords.map((keyword) => (
                      <List.Node
                        key={keyword.name}
                        icon={this.setPanelsIcon(PanelName.SelectedCat)}
                        title={keyword.name}
                        value={keyword}
                        badge={getCount(keyword)}
                      />
                    ))}
                  </List.Node>
                ))}
              </List.Section>
            </>
          )}

          {isEntitiesAnalyzerOn && (
            <>
              <hr className="panel-hr-line" />
              <List.Section
                title="Entities"
                icon={this.setPanelsIcon(PanelName.Entities)}
              >
                {entities.map((entity) => (
                  <List.Node
                    icon={this.setPanelsIcon(PanelName.SelectedCat)}
                    key={entity.name}
                    title={entity.name}
                    value={entity}
                    badge={entity.keywords.reduce(
                      (acc, keyword) => keyword.occurrences.length + acc,
                      0
                    )}
                  >
                    {entity?.keywords?.map((keyword) => (
                      <List.Node
                        key={keyword.text}
                        icon={this.setPanelsIcon(PanelName.SelectedCat)}
                        title={keyword.text}
                        value={keyword}
                        badge={keyword.occurrences.length}
                      />
                    ))}
                  </List.Node>
                ))}
              </List.Section>
            </>
          )}
        </List>
      </Panel>
    );
  }
}

export default connectToMedia((state) => ({
  ...state.media,
  queries: state.query,
}))(Keywords);
